// toastService.js
class ToastService {
  constructor () {
    this.toastContainer = null
    this.defaultConfig = {
      duration: 5000,
      position: 'bottom-right',
      type: 'info'
    }
    this.init()
  }

  init () {
    if (this.toastContainer) return

    // Create toast container
    this.toastContainer = document.createElement('div')
    this.toastContainer.className = 'toast-container'
    this.toastContainer.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-width: 300px;
    `

    document.body.appendChild(this.toastContainer)
  }

  // Check if toasts should be shown
  shouldShowToasts() {
    const stored = localStorage.getItem('toastVisibility');
    return stored ? JSON.parse(stored) : false;
  }

  // Toast types with styles
  getToastStyles (type) {
    const styles = {
      success: {
        background: '#d4edda',
        color: '#155724',
        border: '1px solid #c3e6cb'
      },
      error: {
        background: '#f8d7da',
        color: '#721c24',
        border: '1px solid #f5c6cb'
      },
      warning: {
        background: '#fff3cd',
        color: '#856404',
        border: '1px solid #ffeaa7'
      },
      info: {
        background: '#d1ecf1',
        color: '#0c5460',
        border: '1px solid #bee5eb'
      }
    }
    return styles[type] || styles.info
  }

  show (message, options = {}) {
    // Check if toasts should be shown
    if (!options.force && !this.shouldShowToasts()) {
      return { dismiss: () => {} };
    }

    const config = { ...this.defaultConfig, ...options }

    const toast = document.createElement('div')
    toast.className = `toast toast-${config.type}`

    const styles = this.getToastStyles(config.type)
    toast.style.cssText = `
      padding: 12px 16px;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      cursor: pointer;
      transition: all 0.3s ease;
      opacity: 0;
      transform: translateX(100%);
      background: ${styles.background};
      color: ${styles.color};
      border: ${styles.border};
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.4;
    `

    toast.textContent = message

    this.toastContainer.appendChild(toast)

    // Animate in
    setTimeout(() => {
      toast.style.opacity = '1'
      toast.style.transform = 'translateX(0)'
    }, 10)

    // Auto remove after duration
    const removeToast = () => {
      toast.style.opacity = '0'
      toast.style.transform = 'translateX(100%)'
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast)
        }
      }, 300)
    }

    const timeoutId = setTimeout(removeToast, config.duration)

    // Click to dismiss (and run an optional action, e.g. opening a dialog)
    toast.addEventListener('click', () => {
      clearTimeout(timeoutId)
      removeToast()
      if (typeof config.onClick === 'function') {
        config.onClick()
      }
    })

    return {
      dismiss: () => {
        clearTimeout(timeoutId)
        removeToast()
      }
    }
  }

  // Convenience methods
  success (message, options = {}) {
    return this.show(message, { ...options, type: 'success' })
  }

  error (message, options = {}) {
    return this.show(message, { ...options, type: 'error' })
  }

  warning (message, options = {}) {
    return this.show(message, { ...options, type: 'warning' })
  }

  info (message, options = {}) {
    return this.show(message, { ...options, type: 'info' })
  }

  // Clear all toasts
  clear () {
    while (this.toastContainer.firstChild) {
      this.toastContainer.removeChild(this.toastContainer.firstChild)
    }
  }

  // Destroy the service
  destroy () {
    if (this.toastContainer && this.toastContainer.parentNode) {
      this.toastContainer.parentNode.removeChild(this.toastContainer)
      this.toastContainer = null
    }
  }
}

// Create and export a singleton instance
const toastService = new ToastService()
export { ToastService }
export default toastService